import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Suspense, useEffect, useState } from 'react';
import { RouteFallback } from '@/components/common/RouteFallback';
import { Menu, X } from 'lucide-react';
import { Logo } from '@/components/common/Logo';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { cn } from '@/lib/cn';
import { useAuth, isAdmin } from '@/providers/AuthProvider';

const NAV = [
  { to: '/jobs', label: 'Find work' },
  { to: '/for-doctors', label: 'For doctors' },
  { to: '/for-clinics', label: 'For clinics' },
  { to: '/about', label: 'About' },
];

export function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const portalHref = isAdmin(user?.role) ? '/admin/dashboard' : '/portal/dashboard';

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className={cn(
          'sticky top-0 z-40 transition-all duration-300',
          scrolled ? 'glass border-b border-border' : 'bg-transparent',
        )}
      >
        <div className="container-page flex h-16 items-center justify-between gap-4">
          <Logo />
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'rounded-lg px-3 py-1.5 text-sm font-medium transition',
                    isActive
                      ? 'bg-muted text-ink'
                      : 'text-ink-muted hover:bg-muted hover:text-ink',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <ThemeToggle compact />
            {user ? (
              <ButtonLink to={portalHref} size="sm">
                Go to {isAdmin(user.role) ? 'admin' : 'portal'}
              </ButtonLink>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium text-ink-muted hover:text-ink px-3 py-1.5"
                >
                  Sign in
                </Link>
                <ButtonLink to="/register" size="sm">
                  Join as a doctor
                </ButtonLink>
              </>
            )}
          </div>
          <button
            className="md:hidden rounded-lg p-2 text-ink hover:bg-muted"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-border bg-surface animate-fade-in">
            <div className="container-page py-4 flex flex-col gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-ink hover:bg-muted"
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="mt-2 flex items-center gap-2 pt-2 border-t border-border">
                <ThemeToggle compact />
                <div className="flex-1" />
                {user ? (
                  <ButtonLink to={portalHref} size="sm">Open portal</ButtonLink>
                ) : (
                  <>
                    <Link to="/login" className="text-sm text-ink-muted px-3">Sign in</Link>
                    <ButtonLink to="/register" size="sm">Join</ButtonLink>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </main>

      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border bg-surface mt-24">
      <div className="container-page py-14 grid gap-10 lg:grid-cols-4">
        <div>
          <Logo />
          <p className="mt-4 max-w-xs text-sm text-ink-muted">
            Locum Network places Australian doctors in compliant locum and contract roles, fast.
            Built by people who understand AHPRA, credentialling and life on the floor.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-bg px-3 py-1 text-xs text-ink-muted">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-success" />
            Australian-owned · ABN 27 694 921 616
          </div>
        </div>
        <FooterCol
          title="For doctors"
          links={[
            { to: '/jobs', label: 'Browse jobs' },
            { to: '/for-doctors', label: 'How it works' },
            { to: '/register', label: 'Register' },
            { to: '/login', label: 'Sign in' },
          ]}
        />
        <FooterCol
          title="For clinics"
          links={[
            { to: '/for-clinics', label: 'Clinic enquiry' },
            { to: '/contact', label: 'Contact recruitment' },
          ]}
        />
        <FooterCol
          title="Company"
          links={[
            { to: '/about', label: 'About' },
            { to: '/contact', label: 'Contact' },
            { to: '/privacy', label: 'Privacy' },
            { to: '/terms', label: 'Terms & conditions' },
          ]}
        />
      </div>
      <div className="border-t border-border">
        <div className="container-page py-5 flex flex-col items-start gap-2 text-xs text-ink-subtle sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Locum Network Pty Ltd · ABN 27 694 921 616 · All rights reserved.</p>
          <p>Built for Australian healthcare. AHPRA references are demonstrative only.</p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: Array<{ to: string; label: string }> }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-ink-subtle">{title}</p>
      <ul className="mt-3 space-y-2">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="text-sm text-ink hover:text-primary">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
